import type {
  UploadedFile,
  UploaderFile,
  UploaderMeta,
  UploadSuccessResult,
} from '../types';
import type Uppy from '@uppy/core';
import type { Meta } from '@uppy/core';
import type { TFunction } from 'i18next';

import { useUppyState } from '@uppy/react';
import { cn } from 'cn';
import { useTranslation } from 'react-i18next';
import { ActionsRow } from '~/molecules';

import { useIsUploaderBusy } from '../utils/useIsUploaderBusy';

type P<TMeta extends UploaderMeta = UploaderMeta> = {
  confirmAfterUpload?: boolean;
  confirmButtonText?: (count: number) => string;
  isConfirming?: boolean;
  isDisabled?: boolean;
  isFileReady?: (file: UploaderFile<TMeta>) => boolean;
  onCancel?: () => void;
  onError?: (error: unknown) => void;
  onSuccess?: (result: UploadSuccessResult<TMeta>) => Promise<void> | void;
  showRemoveAllFiles?: boolean;
  uploadButtonText?: string;
  uppy: Uppy<Meta, Record<string, never>>;
} & ComponentStyling;

type PrimaryActionTextOptions = {
  confirmButtonText?: (count: number) => string;
  count: number;
  isUploadComplete: boolean;
  t: TFunction<'ui'>;
  uploadButtonText?: string;
};

function getPrimaryActionText({
  confirmButtonText,
  count,
  isUploadComplete,
  t,
  uploadButtonText,
}: PrimaryActionTextOptions) {
  if (isUploadComplete) {
    return (
      confirmButtonText?.(count) ??
      t(($) => $.uploader.confirmButton, { count })
    );
  }

  return uploadButtonText ?? t(($) => $.uploader.startUploadButton);
}

export function UploadButton<TMeta extends UploaderMeta = UploaderMeta>({
  className,
  confirmAfterUpload = false,
  confirmButtonText,
  isConfirming = false,
  isDisabled = false,
  isFileReady,
  onCancel,
  onError,
  onSuccess,
  showRemoveAllFiles = true,
  style,
  uploadButtonText,
  uppy,
}: P<TMeta>) {
  const { t } = useTranslation('ui');

  const uppyState = useUppyState(uppy, (state) => ({
    files: state.files,
  }));

  const files = Object.values(uppyState.files) as UploaderFile<TMeta>[];
  const hasFiles = files.length > 0;
  const areFilesReady =
    isFileReady === undefined || files.every((file) => isFileReady(file));

  const isBusy = useIsUploaderBusy(uppy, isConfirming);
  const areActionsDisabled = isBusy || isDisabled;

  // A file is pending when it hasn't been uploaded yet and hasn't errored.
  const hasPendingFiles = files.some(
    (f) => !f.progress.uploadComplete && !f.error,
  );

  // Upload is complete only when every file has been uploaded (none are pending or in-flight).
  const isUploadComplete = hasFiles && !hasPendingFiles;

  const handleSuccess = async (
    uploadedFiles: UploaderFile<TMeta>[] = files,
  ) => {
    const result: UploadSuccessResult<TMeta> = uploadedFiles
      .filter((f) => f.progress.uploadComplete)
      .reduce<UploadedFile<TMeta>[]>((acc, f) => {
        const fileId = (f.response?.body as { fileId?: string } | undefined)
          ?.fileId;
        if (!fileId) return acc;
        acc.push({
          fileId,
          fileName: f.name,
          fileSize: f.size,
          meta: f.meta,
          type: f.type ?? '',
        });
        return acc;
      }, []);

    await onSuccess?.(result);
  };

  const handleUpload = async () => {
    let result;
    try {
      result = await uppy.upload();
    } catch (error) {
      onError?.(error);
      return;
    }

    if (!confirmAfterUpload || !result || result.failed?.length) {
      return;
    }

    await handleSuccess(result.successful as UploaderFile<TMeta>[]);
  };

  return (
    <ActionsRow
      className={cn('flex-row-reverse', className)}
      onCancel={onCancel}
      primaryAction={{
        disabled:
          areActionsDisabled ||
          !hasFiles ||
          (!isUploadComplete && !areFilesReady),
        inProgress: isBusy,
        onClick: isUploadComplete ? () => handleSuccess() : handleUpload,
        text: getPrimaryActionText({
          confirmButtonText,
          count: files.length,
          isUploadComplete,
          t,
          uploadButtonText,
        }),
      }}
      secondaryAction={{
        disabled: areActionsDisabled,
        text: t(($) => $.button.secondary.default),
      }}
      style={style}
      tertiaryAction={
        showRemoveAllFiles && hasFiles
          ? {
              buttonStyle: 'link',
              color: 'primary',
              disabled: areActionsDisabled,
              onClick: () => uppy.clear(),
              text: t(($) => $.uploader.removeAllFiles),
            }
          : undefined
      }
    />
  );
}
